import { type Resource } from 'types/api'
import { useQueryClient, useMutation } from '@tanstack/react-query'
import { useApiClient } from 'hooks/useApiClient'
import { type FileImageRequest } from 'types/fileUpload'

interface UsePutImageProps {
  id: number
  onSuccess?: (data: Resource, variables: FileImageRequest) => void
  onError?: (error: Error, variables: FileImageRequest) => void
}

export const usePutImageResource = ({
  id,
  onSuccess,
  onError,
}: UsePutImageProps) => {
  const putAPIClient = useApiClient()
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (resource: FileImageRequest) => {
      const body = new FormData()
      body.set('FileUpload', resource.ImageFile)
      body.set('FileUploadName', resource.ImageFileName)
      body.set('FileUploadType', resource.ImageFileType)

      const { data, error } = await putAPIClient.put(
        '/admin/resources/{id}/uploadimage',
        {
          params: { path: { id } },
          body: {},
          bodySerializer: () => body,
        }
      )
      return { data, error }
    },
    onSuccess: async ({ data, error }, variables: FileImageRequest) => {
      await queryClient.invalidateQueries({
        queryKey: ['/admin/resources', id],
      })
      if (error !== undefined) {
        onError?.(error as Error, variables)
      } else {
        onSuccess?.(data as Resource, variables)
      }
    },
    onError,
  })
}
